import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ETSY_SHOP_URL } from "@/data/products";

export default function About() {
  return (
    <main className="pt-[68px] bg-[#1a1412]" data-testid="about-page">
      {/* Hero */}
      <section className="relative border-b border-[#362b28] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/brand/founder-walking.webp"
            alt=""
            aria-hidden
            className="w-full h-full object-cover object-[50%_25%] opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1412] via-[#1a1412]/70 to-[#1a1412]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-48">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="eyebrow mb-6"
          >
            About · The Origin
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-[52px] md:text-[100px] lg:text-[140px] leading-[0.92] tracking-[-0.02em] text-[#e8dcc4] max-w-[16ch]"
          >
            The <em className="italic text-[#c28b46]">foundation</em> of the human story.
          </motion.h1>
        </div>
      </section>

      {/* Long-form body */}
      <section className="py-28 md:py-40 border-b border-[#362b28]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-12">
          <aside className="col-span-12 md:col-span-3">
            <div className="md:sticky md:top-28">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#c28b46] mb-4">Chapters</p>
              <ol className="space-y-3 text-[13px] text-[#a89b8c]">
                <li><a href="#genesis" className="link-underline">01 · Genesis 2:7</a></li>
                <li><a href="#melanin" className="link-underline">02 · The Science of Melanin</a></li>
                <li><a href="#tribes" className="link-underline">03 · The Same Earth Everywhere</a></li>
                <li><a href="#now" className="link-underline">04 · Now — What This Brand Is</a></li>
              </ol>
            </div>
          </aside>

          <div className="col-span-12 md:col-span-8 md:col-start-5 space-y-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9 }}
              id="genesis"
            >
              <p className="eyebrow mb-4">01</p>
              <h2 className="font-serif text-[36px] md:text-[52px] leading-[1] tracking-tight text-[#e8dcc4] mb-6">
                Genesis 2:7 — <em className="italic text-[#c28b46]">the beginning we forgot.</em>
              </h2>
              <div className="space-y-5 text-[16px] md:text-[17px] leading-relaxed text-[#c8b99c]">
                <p>
                  &ldquo;Then the Lord God formed man of the dust of the ground, and breathed into his nostrils the breath of life; and man became a living soul.&rdquo; Read it slowly. The material is named. It is not abstract. It is earth. Soil. Ground.
                </p>
                <p>
                  What color is that soil, in the places where the first bodies were made? Look at a river bank in the Nile Valley. Look at the earth of West Africa, of Ethiopia, of the Rift Valley — the exact place science and scripture both point to as the origin.
                </p>
                <p>
                  It is <em className="text-[#e8dcc4]">earth-toned</em>. The first bodies were the color of the ground they came from.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9 }}
              id="melanin"
            >
              <p className="eyebrow mb-4">02</p>
              <h2 className="font-serif text-[36px] md:text-[52px] leading-[1] tracking-tight text-[#e8dcc4] mb-6">
                The science of <em className="italic text-[#c28b46]">what you carry.</em>
              </h2>
              <div className="space-y-5 text-[16px] md:text-[17px] leading-relaxed text-[#c8b99c]">
                <p>
                  Melanin is not decoration. It is biological technology. A pigment that converts light, absorbs radiation, protects the neural pathways, and is chemically identical to the pigment found in the human brain, in the tissues of the ear, in the deepest interior of the body.
                </p>
                <p>
                  It is the oldest known biological compound on the planet. Fungi carry it. Plants carry it. And in the human body, it is concentrated in the substantia nigra — literally &ldquo;the dark substance&rdquo; — the seat of movement, memory, and dream.
                </p>
                <p className="font-serif italic text-[18px] md:text-[20px] text-[#e8dcc4] border-l-2 border-[#c28b46] pl-5">
                  You were not given melanin as an accident. You were given it as an inheritance.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9 }}
              id="tribes"
            >
              <p className="eyebrow mb-4">03</p>
              <h2 className="font-serif text-[36px] md:text-[52px] leading-[1] tracking-tight text-[#e8dcc4] mb-6">
                The same story <em className="italic text-[#c28b46]">on every continent.</em>
              </h2>
              <div className="space-y-5 text-[16px] md:text-[17px] leading-relaxed text-[#c8b99c]">
                <p>
                  Look at the indigenous peoples of every continent — before the colonial rewriting. The Aboriginal peoples of Australia. The first peoples of the Americas. The Dravidians. The pre-dynastic Egyptians. The Nubians. They shared the same earth-toned complexion, the same origin, the same DNA thread.
                </p>
                <p>
                  This is not opinion. This is genetics. And it is not about hierarchy — it is about foundation. The story of humanity begins with earth-toned people. Every branch of the human tree traces back to that trunk.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9 }}
              id="now"
            >
              <p className="eyebrow mb-4">04</p>
              <h2 className="font-serif text-[36px] md:text-[52px] leading-[1] tracking-tight text-[#e8dcc4] mb-6">
                So — what is <em className="italic text-[#c28b46]">this brand?</em>
              </h2>
              <div className="space-y-5 text-[16px] md:text-[17px] leading-relaxed text-[#c8b99c]">
                <p>
                  Earth Tone Original Skin is a wearable reminder. Every piece is a sentence in a longer story — a line of scripture, a piece of science, a name that predates the label we were given.
                </p>
                <p>
                  We do not shout. We do not perform. We wear. Because the truth doesn&rsquo;t need volume — it needs to be visible, on the body, moving through the world.
                </p>
                <p>
                  If any of this rings true in you, welcome. The collection is on Etsy. The guide is free. The rest is your journey.
                </p>
              </div>

              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <a
                  href={ETSY_SHOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-terra"
                  data-testid="about-shop-cta"
                >
                  Shop The Collection →
                </a>
                <Link
                  to="/#guide"
                  className="btn-outline-amber"
                  data-testid="about-guide-cta"
                >
                  Get The Free Guide
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
